import { db } from "./firebase";
import {
  doc, getDoc, setDoc, updateDoc, arrayUnion,
  serverTimestamp,
} from "firebase/firestore";

// Firestore structure:
//
// users/{userId}
//   - phone:     "9635007931"
//   - email:     "user@example.com"
//   - name:      "Gourab"
//   - purchases: [{ courseId, courseTitle, orderId, purchasedAt, password }, ...]
//   - createdAt: <timestamp>
//
// Users can be looked up by either phone OR email — we store both keys.
// The unique ID is the phone number (or email if no phone).

// Use email as the unique user ID (since auth is email-based)
const generateUserId = ({ email }) => email.replace(/[^a-z0-9]/gi, "_").toLowerCase();

// ─── Lookup: find a user by phone or email ──────────────────────────────────
export async function getUser({ phone, email }) {
  if (!db) throw new Error("Firebase not configured.");
  const userId = generateUserId({ phone, email });
  const snap   = await getDoc(doc(db, "users", userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ─── Create user (if not exists) ────────────────────────────────────────────
export async function ensureUser({ phone, email, name }) {
  if (!db) throw new Error("Firebase not configured.");
  const userId  = generateUserId({ phone, email });
  const userRef = doc(db, "users", userId);
  const snap    = await getDoc(userRef);

  if (!snap.exists()) {
    await setDoc(userRef, {
      phone:     phone || "",
      email:     email || "",
      name:      name  || "",
      purchases: [],
      createdAt: serverTimestamp(),
    });
  }
  return userId;
}

// ─── Add purchase to user ───────────────────────────────────────────────────
export async function addPurchase({ phone, email, name, course, orderId, pdfPassword }) {
  if (!db) throw new Error("Firebase not configured.");
  const userId  = await ensureUser({ phone, email, name });
  const userRef = doc(db, "users", userId);

  await updateDoc(userRef, {
    purchases: arrayUnion({
      courseId:    course.id,
      courseTitle: course.title,
      driveFileId: course.driveFileId  || "",
      audioFileId: course.audioFileId  || "",
      audioUrl:    course.audioUrl     || "",
      thumbnail:   course.thumbnail    || null,
      orderId,
      amount:      course.price        || 0,
      purchasedAt: new Date().toISOString(),
      pdfPassword: "",
    }),
  });

  return userId;
}

// ─── Get a user's purchases ─────────────────────────────────────────────────
export async function getPurchases({ phone, email }) {
  const user = await getUser({ phone, email });
  return user?.purchases || [];
}
